import { Dialog } from '@base-ui/react';
import { Button, ButtonVariants } from '@components/button/Button';
import { ControlledFormInput } from '@components/formInputs/ControlledFormInput';
import { ControlledFormSelect } from '@components/formInputs/ControlledFormSelect';
import { ControlledFormTextareaAutosize } from '@components/formInputs/ControlledFormTextareaAutosize';
import { Icon } from '@components/icon/Icon';
import { errorToast, successToast } from '@components/toasts/Toasts';
import { ApiError } from '@shared/api/errors';
import { reportAppError } from '@shared/reportAppError';
import { generateCouponCode } from '@shared/utils/generateCouponCode';
import cx from 'classnames';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Drawer } from 'restaurantAdmin/components/drawer/Drawer';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import typography from '~styles/typography.scss';
import { AMOUNT_OFF_TABLE_OPTIONS } from './amountOffTableOptions';
import { CouponExpirationDateField } from './CouponExpirationDateField';
import { createCoupon, type Coupon, updateCoupon } from './apiHelpers';
import styles from './CouponDrawer.scss';

interface CouponFormData {
  name: string;
  code: string;
  amountOffTablePercentage: number;
  expirationDate: string;
  description: string;
}

export interface CouponDrawerProps {
  coupon?: Coupon;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export const CouponDrawer = ({
  coupon,
  isOpen,
  onClose,
  onSaved,
}: CouponDrawerProps) => {
  const { id: restaurantId } = useRestaurant();
  const [isSaving, setIsSaving] = useState(false);
  const isEditing = !!coupon;
  const drawerFieldLabelClassName = cx(
    typography.c2_20,
    styles.drawerFieldLabel,
  );

  const { control, handleSubmit, reset, setValue } = useForm<CouponFormData>({
    defaultValues: {
      name: '',
      code: '',
      amountOffTablePercentage: 10,
      expirationDate: '',
      description: '',
    },
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    reset({
      name: coupon?.name ?? '',
      code: coupon?.code ?? generateCouponCode(),
      amountOffTablePercentage: coupon?.amountOffTablePercentage ?? 10,
      expirationDate: coupon?.expirationDate ?? '',
      description: coupon?.description ?? '',
    });
  }, [coupon, isOpen, reset]);

  const handleSave = handleSubmit(async (data) => {
    setIsSaving(true);

    const payload = {
      name: data.name,
      amountOffTablePercentage: data.amountOffTablePercentage,
      expirationDate: data.expirationDate,
      description: data.description.trim() ? data.description.trim() : null,
    };

    try {
      if (isEditing) {
        await updateCoupon(restaurantId, coupon.id, {
          ...payload,
          code: data.code.trim(),
        });
        successToast({ message: 'Coupon updated successfully' });
      } else {
        await createCoupon(restaurantId, {
          ...payload,
          code: data.code.trim(),
        });
        successToast({ message: 'Coupon created successfully' });
      }
      onSaved();
      onClose();
    } catch (error) {
      if (error instanceof ApiError) {
        errorToast({ message: error.message });
      }
      reportAppError(error);
    } finally {
      setIsSaving(false);
    }
  });

  return (
    <Drawer
      modal
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      side="right"
    >
      <div className={styles.container}>
        <header className={styles.header}>
          <h1 className={cx(typography.h5)}>
            {isEditing ? coupon?.name : 'Add Code'}
          </h1>
          <Dialog.Close
            render={({ onClick }) => (
              <button
                aria-label="Close coupon drawer"
                className={styles.closeButton}
                onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
                type="button"
              >
                <Icon name="close" />
              </button>
            )}
          />
        </header>

        <div className={styles.content}>
          <ControlledFormInput
            control={control}
            label="Name"
            labelClassName={drawerFieldLabelClassName}
            name="name"
            rules={{ required: 'Name is required' }}
            type="text"
          />

          <div className={styles.codeField}>
            <label className={drawerFieldLabelClassName} htmlFor="code">
              Code
            </label>
            <div className={styles.codeRow}>
              <ControlledFormInput
                className={styles.codeInput}
                control={control}
                label="Code"
                labelClassName={styles.visuallyHiddenLabel}
                name="code"
                rules={{ required: 'Code is required' }}
                type="text"
              />
              <Button
                className={styles.generateCodeButton}
                label="Generate Code"
                onClick={() => {
                  setValue('code', generateCouponCode(), { shouldDirty: true });
                }}
                type="button"
                variant={ButtonVariants.Secondary}
              />
            </div>
          </div>

          <div className={styles.twoColumnRow}>
            <ControlledFormSelect
              control={control}
              label="Amount Off Table"
              labelClassName={drawerFieldLabelClassName}
              name="amountOffTablePercentage"
              options={AMOUNT_OFF_TABLE_OPTIONS}
              rules={{ required: 'Amount off table is required' }}
            />
            <CouponExpirationDateField
              control={control}
              name="expirationDate"
            />
          </div>

          <ControlledFormTextareaAutosize
            control={control}
            label="Description"
            labelClassName={drawerFieldLabelClassName}
            name="description"
          />
        </div>

        <footer className={styles.footer}>
          <div className={styles.footerActions}>
            <Dialog.Close
              render={({ onClick }) => (
                <Button
                  isDisabled={isSaving}
                  label="Cancel"
                  onClick={
                    onClick as React.MouseEventHandler<HTMLButtonElement>
                  }
                  variant={ButtonVariants.Tertiary}
                />
              )}
            />
            <Button
              isDisabled={isSaving}
              isLoading={isSaving}
              label="Save"
              onClick={() => {
                void handleSave();
              }}
              variant={ButtonVariants.Primary}
            />
          </div>
        </footer>
      </div>
    </Drawer>
  );
};
